import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {
  selectCarriedCombatResults,
  selectCombatResultsMeleeHold,
  selectIsCityCaptureAnimationPlaying,
  selectIsRuinsIncomeAnimationPending,
  selectIsUnitDeathAnimationPlaying,
  selectShowCityCaptureModal,
  selectShowCombatResultsModal,
  selectShowRoundEndModal,
} from '../reducers/privateStateReducer/selectors';
import {
  selectCombatResults,
  selectCurrentPlayer,
  selectGameResults,
  selectLastCapturedCities,
  selectPhase,
  selectRound,
  selectWarPhaseStage,
} from '../reducers/sharedStateReducer/selectors';

/**
 * Селекторы «модалка ДЕЙСТВИТЕЛЬНО на экране», а не «поднят флаг ее показа».
 * Живут отдельным файлом, потому что смотрят сразу в обе половины стора:
 * флаг показа лежит в private, а данные, без которых модалка не рисуется, —
 * в shared.
 *
 * Разница принципиальна. Флаг гасит только закрытие модалки игроком, а данные
 * под ней перезаписывает каждый следующий расчет боя и любое полное состояние
 * с сервера (LOAD_STATE при переподключении или ресинке). Стоит данным
 * опуститься до пустых — модалка молча исчезает с экрана, а флаг остается
 * поднятым навсегда. Такой невидимый флаг до конца партии блокировал и модалку
 * захвата города, и модалку итогов матча: партия кончалась, а итогов не было,
 * и кнопка меню в шапке (после конца партии она открывает именно итоги)
 * выглядела мертвой.
 *
 * Поэтому очередь (захват города -> бой -> итоги раунда -> анимация дохода руин)
 * уступается только видимым модалкам — каждая следующая смотрит в селекторы
 * «видимости» предыдущих, а не в их флаги.
 */

/**
 * Модалка захвата города ждет облета захваченных городов на поле (см.
 * scenes/CityCaptureAnimation): он и есть первая часть показа захвата. Флаг
 * облета снимает сама анимация — в конце, при обрыве и по сторожевому
 * таймеру, — поэтому навсегда он модалку не спрячет
 */
export const selectIsCityCaptureModalVisible = (state) => Boolean(
  !selectIsCityCaptureAnimationPlaying(state)
  && selectShowCityCaptureModal(state)
  && selectLastCapturedCities(state)?.length
);

/**
 * Итоги стрельбы и ловушек ждут конца ближнего боя, идущего в этом ходу следом:
 * модалка закрыла бы удары и не дала бы выбрать цель и перебросить кости, пока идет
 * время этапа. Придержаны они на этапе, где появились, и на самом бою: сменись этап
 * или игрок мимо боя (полное состояние с сервера), модалка покажется сама
 */
export const selectIsCombatResultsHeldForMelee = (state) => {
  const hold = selectCombatResultsMeleeHold(state);
  if (!hold || selectPhase(state) !== PHASES.WAR || selectCurrentPlayer(state)?.id !== hold.playerId) {
    return false;
  }
  const stage = selectWarPhaseStage(state);
  return stage === hold.stage || stage === WAR_PHASE_STAGES.COMBAT;
};

/**
 * Итоги боя уступают захвату городов конца раунда: облету на поле и его
 * модалке. Бой последнего в войне игрока и захват приходят почти разом, и
 * раньше облет ждал, пока игрок закроет итоги боя, — а модалка держит весь
 * интерфейс: затянул с ней — облет шел уже во время его хода нового раунда,
 * с заблокированным полем. Теперь облет идет сразу, итоги боя — после
 * модалки захвата и до итогов раунда (а уже открытые на это время прячутся и
 * возвращаются). Гибель отрядов на поле идет раньше модалки: иначе она ее закрыла бы.
 * Данные — и итоги прежних этапов, которые игрок еще не закрыл: бой без потерь
 * затирает итоги стрельбы пустыми, но показать их все равно надо
 */
export const selectIsCombatResultsModalVisible = (state) => Boolean(
  !selectIsCombatResultsHeldForMelee(state)
  && !selectIsUnitDeathAnimationPlaying(state)
  && !selectIsCityCaptureAnimationPlaying(state)
  && !selectIsCityCaptureModalVisible(state)
  && selectShowCombatResultsModal(state)
  && (selectCombatResults(state)?.length || selectCarriedCombatResults(state)?.results.length)
);

/**
 * Итоги раунда ждут облета, видимых модалок захвата города и боя. После итогов матча
 * их не показываем: конец партии тоже поднимает флаг итогов раунда (раунд
 * растет), но место итогов раунда занимает модалка итогов матча
 */
export const selectIsRoundEndModalVisible = (state) => Boolean(
  !selectGameResults(state)
  && !selectIsUnitDeathAnimationPlaying(state)
  && !selectIsCombatResultsModalVisible(state)
  && !selectIsCityCaptureAnimationPlaying(state)
  && !selectIsCityCaptureModalVisible(state)
  && selectShowRoundEndModal(state)
  && selectRound(state) >= 1
);

/**
 * Рост дохода руин на поле (см. scenes/RuinsIncomeAnimation) запрашивается тем же
 * переходом раунда, что и итоги раунда, и идет после них — когда закрыты видимые
 * итоги раунда, бой и захват города. После итогов матча не показывается: флаг
 * итогов раунда, оставшийся под ними поднятым, больше ее не держит, а поверх
 * итогов матча она была бы лишней
 */
export const selectIsRuinsIncomeAnimationDue = (state) => Boolean(
  selectIsRuinsIncomeAnimationPending(state)
  && !selectGameResults(state)
  && !selectIsUnitDeathAnimationPlaying(state)
  && !selectIsCombatResultsModalVisible(state)
  && !selectIsCityCaptureAnimationPlaying(state)
  && !selectIsCityCaptureModalVisible(state)
  && !selectIsRoundEndModalVisible(state)
);
